import { useRef, useState } from 'react';
import {
  PHOTO_ACCEPTED_MIME,
  PHOTO_MAX_BYTES,
} from '@camper-diary/shared';
import { uploadPhoto, type Photo } from '../api/photos';
import { HttpError } from '../api/client';

type Props = {
  tripId: string;
  onUploaded: (photo: Photo) => void;
};

type FileStatus =
  | { state: 'queued' }
  | { state: 'uploading' }
  | { state: 'done'; photo: Photo }
  | { state: 'error'; msg: string };

export function PhotoUploader({ tripId, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [statuses, setStatuses] = useState<Map<string, FileStatus>>(new Map());
  const [isDragging, setIsDragging] = useState(false);

  function key(f: File): string {
    return `${f.name}:${f.size}:${f.lastModified}`;
  }

  function setFileStatus(k: string, s: FileStatus) {
    setStatuses((prev) => {
      const next = new Map(prev);
      next.set(k, s);
      return next;
    });
  }

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // pre-segna tutti come queued
    const initial = new Map(statuses);
    for (const f of files) initial.set(key(f), { state: 'queued' });
    setStatuses(initial);

    // upload in serie (più gentile col server e con la rete del telefono)
    for (const f of files) {
      const k = key(f);

      // validation lato client
      if (f.size > PHOTO_MAX_BYTES) {
        setFileStatus(k, {
          state: 'error',
          msg: `File troppo grande (max ${PHOTO_MAX_BYTES / 1024 / 1024} MB)`,
        });
        continue;
      }
      if (
        f.type &&
        !(PHOTO_ACCEPTED_MIME as readonly string[]).includes(f.type) &&
        // alcuni browser non popolano f.type per HEIC: accettiamo upload e lasciamo decidere il server
        f.type.startsWith('image/')
      ) {
        // diamo comunque chance al server (magic-byte detection)
      }

      setFileStatus(k, { state: 'uploading' });
      try {
        const photo = await uploadPhoto(tripId, f);
        setFileStatus(k, { state: 'done', photo });
        onUploaded(photo);
      } catch (err) {
        const msg =
          err instanceof HttpError
            ? err.apiError.message
            : err instanceof Error
              ? err.message
              : 'Errore sconosciuto';
        setFileStatus(k, { state: 'error', msg });
      }
    }
  }

  function onSelectClick() {
    inputRef.current?.click();
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) void handleFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
  }

  const entries = Array.from(statuses.entries());
  const inFlight = entries.filter(([, s]) => s.state === 'uploading' || s.state === 'queued');
  const errors = entries.filter(([, s]) => s.state === 'error');

  return (
    <div
      className={`photo-uploader${isDragging ? ' dragging' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPTED_MIME.join(',')}
        multiple
        hidden
        onChange={onInputChange}
      />
      <div className="uploader-content">
        <p>📷 Trascina foto qui oppure</p>
        <button type="button" className="btn btn-primary" onClick={onSelectClick}>
          Seleziona dal dispositivo
        </button>
        <p className="muted small">
          JPEG, PNG, WebP, GIF · max 25 MB per foto · upload in serie
        </p>
      </div>
      {inFlight.length > 0 && (
        <div className="upload-progress">
          ⏳ {inFlight.length} in corso…
        </div>
      )}
      {errors.length > 0 && (
        <ul className="upload-errors">
          {errors.map(([k, s]) =>
            s.state === 'error' ? (
              <li key={k} className="err">
                {k.split(':')[0]}: {s.msg}
              </li>
            ) : null,
          )}
        </ul>
      )}
    </div>
  );
}
